import { useId, useState, type MouseEvent } from "react";
import { ArrowUpRight, Building2, ChevronDown, Info, Store, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useRouter } from "src/services/Router";
import { accountingPalette } from "./accountingPalette";
import { formatReserveAmount } from "./rollingReserveData";
import type { RollingPeriodReport, RollingPeriodTotal } from "./rollingPeriodReport";

const initialClientLimit = 10;
const { reserve, released } = accountingPalette;

const Notice = styled.div`
	display: flex; gap: 10px; margin-top: 14px; padding: 12px 14px; border: 1px solid #ebcd8f; border-radius: 10px; background: #fdf8ee; color: #344054; font-size: 13px; line-height: 1.5;
	> svg { flex-shrink: 0; margin-top: 2px; color: #925b10; }
	p { margin: 0; min-width: 0; overflow-wrap: anywhere; }
	strong { color: #925b10; font-weight: 600; }
`;

/** The profit card's KPI strip, one per currency. */
const Kpis = styled.dl`
	display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); margin: 0;
	border: 1px solid #e7ebf1; border-radius: 8px; overflow: hidden;
	> div { min-width: 0; padding: 14px 16px; border-right: 1px solid #edf0f4; }
	> div:last-child { border-right: 0; }
	dt { display: flex; align-items: center; gap: 7px; color: #667085; font-size: 12px; line-height: 1.4; }
	dd { margin: 5px 0 0; color: #1d2939; font-size: 20px; font-weight: 600; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
	.dot { width: 8px; height: 8px; flex-shrink: 0; border-radius: 50%; }
	.dot.reserve { background: ${reserve.foreground}; }
	.dot.released { background: ${released.foreground}; }
	dd.reserve { color: ${reserve.foreground}; }
	dd.released { color: ${released.foreground}; }
	.currency { margin-left: 6px; color: #667085; font-size: 13px; font-weight: 500; }
	@media (max-width: 700px) { grid-template-columns: repeat(2, minmax(0, 1fr)); > div { border-bottom: 1px solid #edf0f4; } }
`;

/** Rows follow Payment analysis: subject on the left, amounts in fixed columns so they line up across clients and MIDs. */
const ClientList = styled.div`
	--columns: 34px minmax(0, 1fr) minmax(140px, 210px) minmax(140px, 210px) 28px;
	margin-top: 18px; border: 1px solid #e1e6ef; border-radius: 10px; overflow: hidden; background: #fff; color: #344054; container-type: inline-size;
	.list-head, .row { display: grid; grid-template-columns: var(--columns); column-gap: 14px; align-items: center; padding-inline: 20px 12px; }
	.list-head { padding-block: 10px; background: #fafbfc; color: #667085; font-size: 12px; font-weight: 500; }
	.list-head > :first-child { grid-column: 1 / 3; color: #344054; font-size: 13px; font-weight: 600; }
	.list-head .column { text-align: right; }
	.client { border-top: 1px solid #edf0f5; }
	.client-row { padding-block: 12px; transition: background .15s; }
	.client-row.expandable { cursor: pointer; }
	.client-row:hover { background: #f8fafc; }
	.icon { display: flex; align-items: center; justify-content: center; width: 34px; height: 34px; color: #596579; }
	.subject { min-width: 0; }
	.title { display: flex; align-items: center; gap: 6px; min-width: 0; }
	.title strong { font-size: 14px; font-weight: 600; color: #182230; overflow-wrap: anywhere; }
	.client-link { display: inline-flex; flex-shrink: 0; padding: 2px; border-radius: 4px; color: #98a2b3; }
	.client-link:hover { color: #344054; background: #eef1f6; }
	.client-link:focus-visible { outline: 2px solid #6374d9; outline-offset: 1px; }
	.description { display: block; margin-top: 3px; color: #667085; font-size: 13px; line-height: 1.45; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
	.metric { display: flex; flex-direction: column; gap: 6px; min-width: 0; text-align: right; font-variant-numeric: tabular-nums; }
	.amount strong { font-size: 15px; font-weight: 600; white-space: nowrap; }
	.metric.reserve strong { color: ${reserve.foreground}; }
	.metric.released strong { color: ${released.foreground}; }
	.metric .amount.zero strong { color: #98a2b3; }
	.amount .currency { margin-left: 4px; color: #667085; font-size: 12px; font-weight: 500; }
	.amount small { display: block; margin-top: 2px; color: #667085; font-size: 12px; line-height: 1.4; white-space: nowrap; }
	/* Column headings label the amounts on wide screens; the stacked layout shows these instead. */
	.metric-label { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
	.expand { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; padding: 0; border: 0; border-radius: 6px; background: transparent; color: #98a2b3; cursor: pointer; }
	.expand:hover { background: #eef1f6; color: #344054; }
	.expand:focus-visible { outline: 2px solid #6374d9; outline-offset: 1px; }
	.expand svg { transition: transform .15s; }
	.expand[aria-expanded="true"] svg { transform: rotate(180deg); }
	.mids { background: #f9fafb; }
	.mid-row { padding-block: 9px; border-top: 1px solid #eef1f5; }
	/* A tree line from the client icon down to each MID. */
	.branch { position: relative; align-self: stretch; margin-block: -9px; }
	.branch::before { content: ""; position: absolute; left: 50%; top: 0; bottom: 0; border-left: 1px solid #d0d5dd; }
	.branch::after { content: ""; position: absolute; left: 50%; top: 50%; width: calc(50% + 8px); border-top: 1px solid #d0d5dd; }
	.mid-row:last-child .branch::before { bottom: 50%; }
	.mid-row .title { gap: 7px; }
	.mid-row .title > svg { flex-shrink: 0; color: #98a2b3; }
	.mid-row .title strong { font-size: 13px; font-weight: 500; color: #344054; }
	.mid-row .id { color: #98a2b3; font-size: 12px; white-space: nowrap; font-variant-numeric: tabular-nums; }
	.mid-row .amount strong { font-size: 13px; font-weight: 500; }
	.list-footer { padding: 10px 20px; border-top: 1px solid #edf0f5; background: #fafbfc; }
	.more { padding: 5px 9px; border: 1px solid #ccd4df; border-radius: 7px; background: #fff; color: #364152; font: inherit; font-size: 12px; cursor: pointer; }
	.more:hover { background: #edf1f7; }
	.more:focus-visible { outline: 2px solid #566174; outline-offset: 3px; }
	@media (prefers-reduced-motion: reduce) { .client-row, .expand svg { transition: none; } }
	@container (max-width: 720px) {
		.list-head .column { display: none; }
		.list-head, .row { grid-template-columns: 30px minmax(0, 1fr) minmax(0, 1fr) 28px; column-gap: 10px; row-gap: 8px; padding-inline: 14px 8px; }
		.icon { width: 30px; height: 30px; align-self: start; }
		.branch { grid-row: 1 / span 2; }
		.subject { grid-column: 2 / 4; }
		.metric { grid-row: 2; align-self: start; gap: 4px; text-align: left; }
		.metric.reserve { grid-column: 2; }
		.metric.released { grid-column: 3; }
		.expand { grid-column: 4; grid-row: 1; align-self: start; }
		.amount small { white-space: normal; }
		.metric-label { position: static; width: auto; height: auto; overflow: visible; clip: auto; color: #667085; font-size: 12px; white-space: normal; }
	}
`;

const Empty = styled.p`margin: 14px 0 0; color: #848799; font-size: 13px;`;

export function RollingPeriodReportContent({ report }: { report: RollingPeriodReport }) {
	const { t } = useTranslation();
	const router = useRouter();
	const listId = useId();
	const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());
	const [showAll, setShowAll] = useState(false);
	const counts = new Intl.NumberFormat(t("accountant.locale"));
	const toggle = (id: number) => setExpanded(current => {
		const next = new Set(current);
		if (!next.delete(id)) next.add(id);
		return next;
	});
	// The whole row toggles, except its own controls and a drag that selected an amount to copy.
	const toggleFromRow = (event: MouseEvent<HTMLElement>, id: number) => {
		if ((event.target as HTMLElement).closest("a, button") || window.getSelection()?.toString()) return;
		toggle(id);
	};
	const openClient = (event: MouseEvent<HTMLAnchorElement>, id: number) => {
		if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
		event.preventDefault(); router.navigate("client", { cid: String(id) });
	};
	const metric = (rows: RollingPeriodTotal[], kind: "reserve" | "released") => <span className={`metric ${kind}`}>
		<span className="metric-label">{t(kind === "reserve" ? "accountant.reserved" : "accountant.released")}</span>
		{rows.map(row => {
			const amount = kind === "reserve" ? row.taken : row.released;
			const count = kind === "reserve" ? row.taken_count : row.released_count;
			return <span key={row.currency} className={`amount${amount === 0 ? " zero" : ""}`}>
				<strong>{formatReserveAmount(amount)}</strong><span className="currency">{row.currency}</span>
				<small>{t(kind === "reserve" ? "accountant.rollingTakenValue" : "accountant.rollingReleasedValue", { value: counts.format(count) })}</small>
			</span>;
		})}
	</span>;
	const notes = report.notes.filter(note => !report.coverage.partial || !note.includes("requested period is not fully covered"));
	const clients = showAll ? report.clients : report.clients.slice(0, initialClientLimit);

	return <div style={{ minWidth: 0 }}>
		{report.coverage.partial && <Notice role="status"><TriangleAlert size={18} aria-hidden="true" /><p><strong>{t("accountant.rollingPartialTitle")}</strong> {t("accountant.rollingPartial", { from: report.coverage.oldest ?? "—", to: report.coverage.newest ?? "—" })}</p></Notice>}
		{notes.map((note, index) => <Notice key={index}><Info size={18} aria-hidden="true" /><p>{note}</p></Notice>)}
		{report.totals.length ? <div style={{ display: "grid", gap: 8, marginTop: 16 }}>
			{report.totals.map(row => <Kpis key={row.currency} aria-label={t("accountant.reserveAria", { currency: row.currency })}>
				<div><dt><span className="dot reserve" aria-hidden="true" />{t("accountant.reserved")}</dt><dd className="reserve">{formatReserveAmount(row.taken)}<span className="currency">{row.currency}</span></dd></div>
				<div><dt><span className="dot released" aria-hidden="true" />{t("accountant.released")}</dt><dd className="released">{formatReserveAmount(row.released)}<span className="currency">{row.currency}</span></dd></div>
				<div><dt>{t("accountant.rollingTakenCount")}</dt><dd>{counts.format(row.taken_count)}</dd></div>
				<div><dt>{t("accountant.rollingReleasedCount")}</dt><dd>{counts.format(row.released_count)}</dd></div>
			</Kpis>)}
		</div> : <Empty>{t("accountant.rollingEmpty")}</Empty>}
		{report.clients.length > 0 && <ClientList>
			<div className="list-head" aria-hidden="true"><span>{t("accountant.clients", { count: report.clients.length })}</span><span className="column">{t("accountant.reserved")}</span><span className="column">{t("accountant.released")}</span></div>
			<div id={listId} role="list" aria-label={t("accountant.clients", { count: report.clients.length })}>
				{clients.map(client => {
					const open = expanded.has(client.id), midsId = `${listId}-${client.id}`;
					return <div key={client.id} className="client" role="listitem">
						<div className={`row client-row${client.mids.length ? " expandable" : ""}`} onClick={client.mids.length ? event => toggleFromRow(event, client.id) : undefined}>
							<span className="icon" aria-hidden="true"><Building2 size={19} /></span>
							<span className="subject">
								<span className="title"><strong>{client.name}</strong><a className="client-link" href={`/clients/${client.id}`} title={t("accountant.rollingOpenClient")} aria-label={`${t("accountant.rollingOpenClient")}: ${client.name}`} onClick={event => openClient(event, client.id)}><ArrowUpRight size={15} aria-hidden="true" /></a></span>
								<span className="description">#{client.id} · {t("accountant.rollingMids", { value: counts.format(client.mids.length) })}</span>
							</span>
							{metric(client.currencies, "reserve")}
							{metric(client.currencies, "released")}
							{client.mids.length > 0 && <button type="button" className="expand" aria-expanded={open} aria-controls={midsId} aria-label={t(open ? "accountant.rollingHideMids" : "accountant.rollingShowMids", { name: client.name })} onClick={() => toggle(client.id)}><ChevronDown size={17} aria-hidden="true" /></button>}
						</div>
						<div id={midsId} className="mids" hidden={!open}>
							{open && client.mids.map(mid => <div key={mid.id} className="row mid-row">
								<span className="branch" aria-hidden="true" />
								<span className="subject"><span className="title"><Store size={14} aria-hidden="true" /><strong>{mid.name}</strong><span className="id">MID #{mid.id}</span></span></span>
								{metric(mid.currencies, "reserve")}
								{metric(mid.currencies, "released")}
							</div>)}
						</div>
					</div>;
				})}
			</div>
			{report.clients.length > initialClientLimit && <div className="list-footer"><button type="button" className="more" aria-controls={listId} aria-expanded={showAll} onClick={() => setShowAll(value => !value)}>{showAll ? t("analysis.collapseToTen") : t("analysis.showAll", { count: report.clients.length })}</button></div>}
		</ClientList>}
	</div>;
}
